import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import ReactMap, { Marker, Popup } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  fetchNearbyPinsRequest,
  setUserLocation,
  setMapCenter,
  selectPin,
  clearSelectedPin
} from '../redux/map/actions';
import { playTrackRequest } from '../redux/audio/actions';
import { updateTrackLocation } from '../redux/audio/api';
import { logout } from '../redux/auth/actions';
import { doFetch, clearMe } from '../redux/api/actions';
import { ME } from '../redux/api/constants';
import SearchBar from './SearchBar';
import MapAddTrackModal from './MapAddTrackModal';
import TrackCommentsPanel from './TrackCommentsPanel';
import api_endpoint from '../settings';
import { useTranslation } from 'react-i18next';
import './MapView.css';

// Mapbox access token (you'll need to set this in environment variables)
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoidG9yZ3lkIiwiYSI6ImNtNXN6ZGQ0bjBhbGIya3M5dHpwY2p3aDkifQ.placeholder';

const getTourCategoryIcon = (category) => {
  switch ((category || '').trim().toLowerCase()) {
    case 'ghost tour':
      return 'bi-moon-stars-fill';
    case 'historical':
      return 'bi-clock-fill';
    case 'pop culture':
      return 'bi-star-fill';
    case 'family history':
      return 'bi-people-fill';
    case 'food & drink':
      return 'bi-cup-hot-fill';
    case 'architecture':
      return 'bi-building-fill';
    case 'nature & parks':
      return 'bi-tree-fill';
    case 'art & culture':
      return 'bi-palette-fill';
    default:
      return 'bi-geo-alt-fill';
  }
};

const MapView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mapRef = useRef();
  const [searchParams] = useSearchParams();

  const paramLat = parseFloat(searchParams.get('lat'));
  const paramLng = parseFloat(searchParams.get('lng'));
  const paramZoom = parseFloat(searchParams.get('zoom'));
  const hasLocationParams = !isNaN(paramLat) && !isNaN(paramLng);

  // Redux state
  const {
    pins,
    userLocation,
    selectedPin,
    pinsLoading
  } = useSelector(state => state.map);

  const accessToken = useSelector(state => state.Auth.accessToken);
  const me = useSelector(state => state.API.me);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Admin flag — drives draggable pin behaviour
  const isAdmin = Boolean(me?.is_superuser);

  // Optimistic lat/lng overrides applied after an admin drag-drop.
  // Shape: { [trackId]: { lat, lng } }
  const [pinPositionOverrides, setPinPositionOverrides] = useState({});

  // Social (thumbs up / comments) state for selected pin
  const [social, setSocial] = useState(null);
  const [showComments, setShowComments] = useState(false);

  // Pin dropping state (long press / right-click only)
  const [tempPin, setTempPin] = useState(null);
  const [showAddTrackModal, setShowAddTrackModal] = useState(false);
  const clickCoords = useRef(null);
  const isDragging = useRef(false);
  // Long-press detection: timer fires after 500ms of stationary hold
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  // Local state for viewport
  const [viewState, setViewState] = useState({
    longitude: hasLocationParams ? paramLng : -74.006,
    latitude: hasLocationParams ? paramLat : 40.7128,
    zoom: hasLocationParams ? (paramZoom || 15) : 13
  });

  const tourSummaries = useMemo(() => {
    const toursById = new Map();
    pins.forEach((pin) => {
      const existing = toursById.get(pin.tourId);
      const duration = pin.durationSeconds || 0;
      if (existing) {
        existing.stopCount += 1;
        existing.totalDurationSeconds += duration;
        existing.closestDistance = Math.min(existing.closestDistance, pin.distance ?? Infinity);
        existing.pins.push(pin);
      } else {
        toursById.set(pin.tourId, {
          tourId: pin.tourId,
          tourTitle: pin.tourTitle,
          tourCategory: pin.tourCategory,
          pinColor: pin.pinColor || '#FF5733',
          pinEmoji: pin.pinEmoji || '📍',
          stopCount: 1,
          totalDurationSeconds: duration,
          closestDistance: pin.distance ?? Infinity,
          firstPin: pin,
          pins: [pin],
        });
      }
    });

    return Array.from(toursById.values())
      .sort((a, b) => a.closestDistance - b.closestDistance)
      .slice(0, 8);
  }, [pins]);

  const formatTourDuration = useCallback((seconds) => {
    if (!seconds) return t('text-tour');
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem ? `${hours} ${t('hours-label')} ${rem} min` : `${hours} ${t('hours-label')}`;
  }, [t]);

  // Fetch user profile when access token changes
  useEffect(() => {
    if (accessToken) {
      dispatch(doFetch(ME, 'users/me', accessToken));
    } else {
      dispatch(clearMe());
    }
  }, [dispatch, accessToken]);

  // Fetch social counts when selected pin changes
  useEffect(() => {
    if (!selectedPin) {
      setSocial(null);
      setShowComments(false);
      return;
    }
    const headers = accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {};
    fetch(`${api_endpoint}/audio/tracks/${selectedPin.trackId}/social`, { headers })
      .then(r => r.json())
      .then(setSocial)
      .catch(() => setSocial({ thumbsupCount: 0, commentCount: 0, userHasThumbsedUp: false }));
  }, [selectedPin, accessToken]);

  // Get user's geolocation on mount (skipped if URL params provided)
  useEffect(() => {
    if (hasLocationParams) {
      dispatch(fetchNearbyPinsRequest(paramLat, paramLng, 10));
      return;
    }

    const fallbackToIPGeolocation = async () => {
      try {
        // Use IP-based geolocation as fallback
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.latitude && data.longitude) {
          const { latitude, longitude } = data;
          console.log(`IP geolocation: ${data.city}, ${data.region}, ${data.country_name}`);

          setViewState({
            longitude,
            latitude,
            zoom: 13
          });
          dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
        } else {
          // Final fallback to NYC
          console.log('IP geolocation failed, defaulting to NYC');
          dispatch(fetchNearbyPinsRequest(40.7128, -74.006, 10));
        }
      } catch (error) {
        console.error('IP geolocation error:', error);
        // Final fallback to NYC
        dispatch(fetchNearbyPinsRequest(40.7128, -74.006, 10));
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('Browser geolocation successful');
          dispatch(setUserLocation(latitude, longitude));
          setViewState({
            longitude,
            latitude,
            zoom: 13
          });
          // Fetch nearby pins
          dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
        },
        (error) => {
          console.log('Browser geolocation denied/failed, trying IP geolocation');
          // Fallback to IP-based geolocation
          fallbackToIPGeolocation();
        }
      );
    } else {
      // Browser doesn't support geolocation, use IP fallback
      console.log('Browser geolocation not supported, using IP geolocation');
      fallbackToIPGeolocation();
    }
  }, [dispatch, hasLocationParams, paramLat, paramLng]);

  // Handle map move end - fetch pins for new area
  const handleMoveEnd = useCallback((evt) => {
    const { longitude, latitude } = evt.viewState;
    dispatch(setMapCenter(latitude, longitude));
    // Fetch new pins for this area
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
  }, [dispatch]);

  // Handle pin click — cancel any pending long-press so the "Add Stop" modal
  // does not fire when the user single-clicks an existing audio track pin.
  const handlePinClick = useCallback((pin, e) => {
    e.originalEvent.stopPropagation();

    // Prevent the map-level mouseup handler from treating this as a long press
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressFired.current = false;
    clickCoords.current = null;

    dispatch(selectPin(pin));
  }, [dispatch]);

  const handleTourCardClick = useCallback((tour) => {
    const firstPin = tour.firstPin;
    if (!firstPin) return;

    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [firstPin.lng, firstPin.lat],
        zoom: 16,
        duration: 900,
        essential: true,
      });
    }

    dispatch(selectPin(firstPin));
  }, [dispatch]);


  // Handle mouse down - record position and start long-press timer (500ms).
  // The "Add Stop" modal should only appear on a long press, never on a
  // normal single click, so regular listeners never see it accidentally.
  const handleMouseDown = useCallback((event) => {
    clickCoords.current = event.lngLat;
    isDragging.current = false;
    longPressFired.current = false;

    // Cancel any stale timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    // Only start the timer when the user is logged in (guides need this feature)
    if (accessToken && me) {
      const coords = event.lngLat; // capture at mousedown time
      longPressTimer.current = setTimeout(() => {
        // Only open if the pointer hasn't moved (no drag)
        if (!isDragging.current && clickCoords.current) {
          longPressFired.current = true;
          setTempPin({ lng: coords.lng, lat: coords.lat });
          setShowAddTrackModal(true);
        }
        longPressTimer.current = null;
      }, 500);
    }
  }, [accessToken, me]);

  // Use Mapbox's native drag events (authoritative) to detect drags.
  // This is wired up in the onLoad handler below via map.on('dragstart'/'dragend').
  // The mousemove pixel-distance approach was unreliable because Mapbox consumes
  // pointer events before React's synthetic mousemove fires during a drag.

  // Handle mouse up - cancel the long-press timer (user released before 500ms).
  // Do NOT open the "Add Stop" modal on regular clicks.
  const handleMouseUp = useCallback((event) => {
    // Cancel the long-press timer if still pending
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }

    // Clean up tracking refs
    clickCoords.current = null;
    isDragging.current = false;
    longPressFired.current = false;
  }, []);

  // Wire up Mapbox's authoritative drag events once the map instance is ready.
  // dragstart fires the moment Mapbox takes ownership of the pointer for panning,
  // which is earlier and more reliable than tracking mousemove pixel distance.
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    map.on('dragstart', () => {
      isDragging.current = true;
      // Cancel any pending long-press timer immediately
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
      clickCoords.current = null;
    });

    map.on('dragend', () => {
      isDragging.current = false;
    });
  }, []);

  // Handle "Play" button click in popup
  const handlePlayTrack = useCallback((pin) => {
    dispatch(playTrackRequest(pin.trackId, pin.tourId));
    dispatch(clearSelectedPin());
  }, [dispatch]);

  // Handle admin drag-drop of a pin.
  // Applies an optimistic local position override immediately, then PATCHes the backend.
  // The override keeps the marker at the dropped location even before the next
  // fetchNearbyPins response arrives. On error the override is reverted.
  const handlePinDragEnd = useCallback(async (pin, event) => {
    const { lng, lat } = event.lngLat;

    // Optimistic update
    setPinPositionOverrides(prev => ({ ...prev, [pin.trackId]: { lat, lng } }));

    try {
      await updateTrackLocation(pin.trackId, lat, lng, accessToken);
      console.log(`Pin ${pin.trackId} moved to (${lat.toFixed(6)}, ${lng.toFixed(6)})`);
    } catch (err) {
      // Revert optimistic update on failure
      setPinPositionOverrides(prev => {
        const next = { ...prev };
        delete next[pin.trackId];
        return next;
      });
      console.error('Failed to save pin location:', err);
      alert(t('map-pin-save-error'));
    }
  }, [accessToken, t]);

  const handleThumbsUp = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${api_endpoint}/audio/tracks/${selectedPin.trackId}/thumbsup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });
      const data = await res.json();
      setSocial(prev => ({ ...prev, thumbsupCount: data.thumbsupCount, userHasThumbsedUp: data.thumbsedUp }));
    } catch (e) {
      // silent fail
    }
  }, [accessToken, selectedPin]);

  // Handle city search result selection
  const handleCitySelect = useCallback((cityData) => {
    const { longitude, latitude, cityName } = cityData;

    // Animate map to the selected city
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: 13,
        duration: 2000, // 2 second animation
        essential: true, // Animation can be interrupted by user
        curve: 1.42, // Smooth easing curve
      });
    }

    // Update map center in Redux
    dispatch(setMapCenter(latitude, longitude));

    // Fetch pins for the new city
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));

    // Clear any selected pin
    dispatch(clearSelectedPin());

    // Optional: Log the city search for analytics
    console.log(`User searched for: ${cityName}`);
  }, [dispatch]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 20)
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, 0)
    }));
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    dispatch(logout());
    setShowUserMenu(false);
  }, [dispatch]);

  // Handle successful track creation
  const handleTrackAdded = useCallback((trackData) => {
    // Clear temp pin
    setTempPin(null);

    // Refresh pins in the current map area
    const { latitude, longitude } = viewState;
    dispatch(fetchNearbyPinsRequest(latitude, longitude, 10));
  }, [dispatch, viewState]);

  return (
    <div className="map-view-container">
      {/* Google Maps-style navbar with integrated search */}
      <div className="map-navbar">
        <Link to="/" className="map-brand">
          <span className="map-brand-mark">T</span>
          <span className="map-brand-word">Torgyd</span>
        </Link>

        {/* Search bar integrated into navbar */}
        <SearchBar onResultSelect={handleCitySelect} />

        <div className="map-nav-actions">
          {accessToken && me ? (
            <>
              <div className="map-user-menu">
                <button
                  className="map-user-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                >
                  <i className="bi bi-person-circle"></i>
                  <span>{me.email?.split('@')[0]}</span>
                </button>
                {showUserMenu && (
                  <div className="map-user-dropdown">
                    <Link to="/tourguide" onClick={() => setShowUserMenu(false)}>
                      {t('dashboard')}
                    </Link>
                    <Link to="/tourguide/tours/create" onClick={() => setShowUserMenu(false)}>
                      {t('tourguide-create-tour')}
                    </Link>
                    {me.is_superuser && (
                      <Link to="/admin" onClick={() => setShowUserMenu(false)}>
                        {t('admin')}
                      </Link>
                    )}
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout}>{t('logout')}</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/signin" className="map-nav-btn">{t('signin')}</Link>
              <Link to="/signup" className="map-nav-btn-primary">{t('create')}</Link>
            </>
          )}
        </div>
      </div>

      <aside className="discovery-panel" aria-label={t('map-audio-tours-around-you')}>
        <div className="discovery-panel-header">
          <div>
            <p className="discovery-eyebrow">{t('map-explore-nearby')}</p>
            <h1>{t('map-audio-tours-around-you')}</h1>
          </div>
          <Link to={accessToken ? '/tourguide/tours/create' : '/signup'} className="discovery-create-btn">
            <i className="bi bi-plus-lg"></i>
            <span>{t('create')}</span>
          </Link>
        </div>

        <div className="discovery-stats">
          <div>
            <strong>{pins.length}</strong>
            <span>{t('stops-label')}</span>
          </div>
          <div>
            <strong>{tourSummaries.length}</strong>
            <span>{t('tours-label')}</span>
          </div>
          <div>
            <strong>10km</strong>
            <span>{t('radius-label')}</span>
          </div>
        </div>

        <div className="discovery-list">
          {tourSummaries.length > 0 ? (
            tourSummaries.map((tour) => (
              <button
                key={tour.tourId}
                type="button"
                className="discovery-card"
                onClick={() => handleTourCardClick(tour)}
              >
                <span className="discovery-card-pin" style={{ backgroundColor: tour.pinColor }}>
                  {tour.pinEmoji}
                </span>
                <span className="discovery-card-body">
                  <span className="discovery-card-title">{tour.tourTitle}</span>
                  <span className="discovery-card-meta">
                    {tour.tourCategory ? `${tour.tourCategory} · ` : ''}
                    {t('stops-count', { count: tour.stopCount })} · {formatTourDuration(tour.totalDurationSeconds)}
                    {Number.isFinite(tour.closestDistance) ? ` · ${tour.closestDistance.toFixed(1)}km` : ''}
                  </span>
                </span>
                <i className="bi bi-chevron-right"></i>
              </button>
            ))
          ) : (
            <div className="discovery-empty">
              <i className="bi bi-compass"></i>
              <h2>{t('map-no-audio-tours')}</h2>
              <p>{t('map-empty-description')}</p>
              <Link to={accessToken ? '/tourguide/tours/create' : '/signup'} className="discovery-empty-link">
                {t('map-start-tour')}
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Zoom controls */}
      <div className="zoom-controls">
        <button
          className="zoom-btn zoom-in"
          onClick={handleZoomIn}
          title={t('map-zoom-in')}
        >
          +
        </button>
        <button
          className="zoom-btn zoom-out"
          onClick={handleZoomOut}
          title={t('map-zoom-out')}
        >
          −
        </button>
      </div>

      <ReactMap
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onMoveEnd={handleMoveEnd}
        onLoad={handleMapLoad}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100vh' }}
      >
        {/* User location marker */}
        {userLocation && (
          <Marker
            longitude={userLocation.lng}
            latitude={userLocation.lat}
            anchor="center"
          >
            <div className="user-location-marker">
              <div className="pulse-ring"></div>
              <div className="user-dot"></div>
            </div>
          </Marker>
        )}

        {/* Temporary pin (before saving) */}
        {tempPin && (
          <Marker
            longitude={tempPin.lng}
            latitude={tempPin.lat}
            anchor="bottom"
          >
            <div
              className="custom-marker temp-marker"
              style={{ backgroundColor: '#4CAF50' }}
            >
              <span className="marker-emoji">📍</span>
            </div>
          </Marker>
        )}

        {/* Audio track pins */}
        {pins.map((pin) => {
          // Use optimistic override position if admin has dragged this pin
          const override = pinPositionOverrides[pin.trackId];
          const effectiveLat = override ? override.lat : pin.lat;
          const effectiveLng = override ? override.lng : pin.lng;

          return (
            <Marker
              key={pin.trackId}
              longitude={effectiveLng}
              latitude={effectiveLat}
              anchor="bottom"
              onClick={(e) => handlePinClick(pin, e)}
              // Admin pins are draggable; cursor hint applied via CSS class
              draggable={isAdmin}
              onDragEnd={isAdmin ? (e) => handlePinDragEnd(pin, e) : undefined}
            >
              <div
                className={`custom-marker${isAdmin ? ' custom-marker--draggable' : ''}`}
                style={{
                  backgroundColor: pin.pinColor || '#FF5733',
                }}
                title={isAdmin ? t('map-drag-to-reposition', { title: pin.trackTitle }) : pin.trackTitle}
              >
                <span className="marker-emoji">{pin.pinEmoji || '📍'}</span>
                {pin.tourCategory && (
                  <span
                    className="marker-category-badge"
                    title={pin.tourCategory}
                    style={{ color: pin.pinColor || '#FF5733' }}
                  >
                    <i className={`bi ${getTourCategoryIcon(pin.tourCategory)}`}></i>
                  </span>
                )}
              </div>
            </Marker>
          );
        })}

        {/* Selected pin popup */}
        {selectedPin && (
          <Popup
            longitude={selectedPin.lng}
            latitude={selectedPin.lat}
            anchor="bottom"
            onClose={() => dispatch(clearSelectedPin())}
            closeButton={true}
            closeOnClick={false}
            offset={25}
          >
            <div className="pin-popup">
              <h3>{selectedPin.trackTitle}</h3>
              <p className="tour-title">{selectedPin.tourTitle}</p>
              <div className="popup-meta">
                {selectedPin.tourCategory && (
                  <span className="category">
                    <i className={`bi ${getTourCategoryIcon(selectedPin.tourCategory)}`}></i>
                    {selectedPin.tourCategory}
                  </span>
                )}
                <span className="duration">
                  {selectedPin.durationSeconds
                    ? `${Math.floor(selectedPin.durationSeconds / 60)}min ${selectedPin.durationSeconds % 60}s`
                    : t('text-stop')}
                </span>
                {selectedPin.distance !== undefined && (
                  <span className="distance">{t('map-km-away', { distance: selectedPin.distance.toFixed(2) })}</span>
                )}
                {selectedPin.sequenceOrder > 0 && (
                  <span className="sequence">{t('map-stop-number', { number: selectedPin.sequenceOrder })}</span>
                )}
              </div>
              <div className="popup-social">
                <button
                  className={`social-btn thumbsup-btn${social?.userHasThumbsedUp ? ' active' : ''}`}
                  onClick={handleThumbsUp}
                  disabled={!accessToken}
                  title={accessToken ? t('map-thumbs-up') : t('map-signin-rate')}
                >
                  👍 <span>{social?.thumbsupCount ?? '—'}</span>
                </button>
                <button
                  className="social-btn comments-btn"
                  onClick={() => setShowComments(true)}
                >
                  💬 <span>{social?.commentCount ?? '—'}</span>
                </button>
              </div>
              <div className="popup-actions-row">
                {accessToken ? (
                  <button
                    className="play-button"
                    onClick={() => handlePlayTrack(selectedPin)}
                  >
                    ▶ {t('play')}
                  </button>
                ) : (
                  <Link className="play-button play-button-link" to="/signin">
                    {t('signin')}
                  </Link>
                )}
                <Link className="tour-detail-button" to={`/tours/${selectedPin.tourId}`}>
                  {t('map-view-tour')}
                </Link>
              </div>
            </div>
          </Popup>
        )}
      </ReactMap>

      {/* Loading overlay */}
      {pinsLoading && (
        <div className="map-loading-overlay">
          <div className="spinner"></div>
          <p>{t('map-loading-tracks')}</p>
        </div>
      )}

      {/* Comments side panel */}
      {showComments && selectedPin && (
        <TrackCommentsPanel
          trackId={selectedPin.trackId}
          trackTitle={selectedPin.trackTitle}
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Add Track Modal */}
      {tempPin && (
        <MapAddTrackModal
          isOpen={showAddTrackModal}
          onClose={() => {
            setShowAddTrackModal(false);
            setTempPin(null);
          }}
          latitude={tempPin.lat}
          longitude={tempPin.lng}
          onSuccess={handleTrackAdded}
        />
      )}
    </div>
  );
};

export default MapView;
